import { ButtonGroup } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';
import { DateRange } from '@mui/x-date-pickers-pro';
import { EnhancedDataGrid } from 'components/enhanced/data-grid';
import { EnhancedDateRangePicker } from 'components/enhanced/form/adapters/date-time/EnhancedDateRangePicker';
import { useIsTechnician } from 'shared/permissions/useIsTechnician';
import { useIsTechnicianCoordinator } from 'shared/permissions/useIsTechnicianCoordinator';
import { useIsTenantAdministrator } from 'shared/permissions/IsTenantAdministrator';
import AddEnhancedButton from 'components/ui/buttons/AddEnhancedButton';
import LinkEnhancedButton from 'components/ui/buttons/LinkEnhancedButton';
import CollapsableColumns from 'components/ui/CollapsableColumns';
import { ROLE_SUPER_ADMIN, ROLE_TENANT_ADMIN } from 'shared/permissions/roles';
import dayjs, { Dayjs } from 'dayjs';
import TenantPage from 'layouts/TenantPage';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'store/store';
import { Option } from 'shared/types/general';
import { DeleteFilteredMovementsButton } from '../components/dialog/DeleteMovementsByFilterDialog'; // Adjust the import path as necessary
import ProductBatchDialogDialog from '../components/dialog/ProductBatchDialog';
import ManualMovement from '../components/form/ManualMovementCreateForm';
import { useMovementSubmission } from '../hooks/useMovementSubmission';
import { typeService } from 'shared/services';
import { movementReasonService } from '../api/movement-reason.service';
import { movementService } from '../api/movement.service';
import { unapprovedMovementService } from '../api/unapproved-movement.service';
import { labelThresholdService } from 'features/inventory/warehouses/api/label-threshold.service';
import { MovementsColumns } from '../movements';

export const Movements = () => {
  const { t } = useTranslation();
  const translationPrefix = 'pages.movements';

  const dataGridRefreshRef = useRef(null);

  const isSapActive = useAppSelector((state) => state.auth.sap_active);
  const userGroups = useAppSelector((state) => state.auth.groups);

  const shouldHideTenantAdminSections =
    userGroups.includes(ROLE_TENANT_ADMIN) && !userGroups.includes(ROLE_SUPER_ADMIN);
  const canViewUnapprovedMovements = useIsTechnicianCoordinator() || useIsTenantAdministrator();
  const isEditDisabled = useIsTechnician() || useIsTechnicianCoordinator();

  const [movementReasonOptions, setMovementReasonOptions] = useState<Option[]>([]);
  const [isManualMovementFormOpen, setManualMovementFormOpen] = useState(false);
  const [pendingMovementCount, setPendingMovementCount] = useState(0);
  const [productTypeOptions, setProductTypeOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [labelThresholdOptions, setLabelThresholdOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);

  const columns: GridColDef[] = MovementsColumns(
    (movementReasonOptions || []).map((reason) => reason.code),
    false,
    false,
    false,
    undefined,
    false,
    isSapActive,
    true,
    productTypeOptions || [],
    labelThresholdOptions || []
  );
  // ------------------- ENHANCED DATE PICKER --------------------
  const today = dayjs();
  const oneWeekAgo = today.subtract(7, 'days');
  const [dateRange, setDateRange] = useState<DateRange<Dayjs>>([
    dayjs(`${oneWeekAgo.year()}-${oneWeekAgo.month() + 1}-${oneWeekAgo.date()}`),
    dayjs(`${today.year()}-${today.month() + 1}-${today.date()}`)
  ]);

  const [movementQueryParams, setMovementQueryParams] = useState<Record<string, string>>({
    timestamp__gte: dateRange[0]?.format('YYYY-MM-DD'),
    timestamp__lte: dateRange[1]?.format('YYYY-MM-DD')
  });

  const handleDateRangeChange = (date: DateRange<Dayjs>) => {
    if (date[0] && date[1]) setDateRange([date[0], date[1]]);
  };

  const handleDateRangeAccept = (date: DateRange<Dayjs>) => {
    setMovementQueryParams({
      timestamp__gte: date[0]?.format('YYYY-MM-DD'),
      timestamp__lte: dateRange[1]?.format('YYYY-MM-DD')
    });
  };
  // ------------------------------------------------------------

  useEffect(() => {
    unapprovedMovementService
      .getAllPaginated()
      .then((res) => {
        setPendingMovementCount(res?.count || 0);
      })
      .catch(() => {
        setPendingMovementCount(0);
      });
    movementReasonService
      .getAllPaginated()
      .then((res) => {
        setMovementReasonOptions(res?.results || []);
      })
      .catch(() => {
        setMovementReasonOptions([]);
      });
    typeService
      .getAllBaseInformation()
      .then((types: Option[] | undefined) => {
        if (types && Array.isArray(types)) {
          setProductTypeOptions(
            types.map((type) => ({
              value: type.code,
              label: type['description'] || type.label || type.code
            }))
          );
        } else {
          setProductTypeOptions([]);
        }
      })
      .catch(() => {
        setProductTypeOptions([]);
      });
    labelThresholdService
      .getAllBaseInformation()
      .then((labels: Option[] | undefined) => {
        if (labels && Array.isArray(labels)) {
          setLabelThresholdOptions(
            labels.map((label) => ({
              value: label.code,
              label: label.code
            }))
          );
        } else {
          setLabelThresholdOptions([]);
        }
      })
      .catch(() => {
        setLabelThresholdOptions([]);
      });
  }, []);

  const {
    selectedMovement,
    batchExpirationOptions,
    isProductBatchDialogOpen,
    selectedBatchId,
    handleMovementSubmit,
    submitMovement,
    closeProductBatchDialog,
    setSelectedBatchId
  } = useMovementSubmission(dataGridRefreshRef);

  return (
    <TenantPage
      title={t(`${translationPrefix}.title`)}
      subtitle={t(`${translationPrefix}.subtitle`)}
      pageType="movements"
      menuRight={
        <ButtonGroup>
          <LinkEnhancedButton
            label={t(`${translationPrefix}.go-to-uninvoiced`)}
            to="uninvoiced"
            requirePermission={'tenantAdmin'}
          />
          <LinkEnhancedButton
            label={t(`${translationPrefix}.to_be_approved`)}
            count={pendingMovementCount}
            to="unapproved"
            visible={canViewUnapprovedMovements}
          />
          <LinkEnhancedButton
            label={t(`${translationPrefix}.reasons`)}
            to="reasons"
            requirePermission={'tenantAdmin'}
          />
          <AddEnhancedButton
            prefix={translationPrefix}
            onClick={() => setManualMovementFormOpen(!isManualMovementFormOpen)}
            visible={!isEditDisabled}
          />
        </ButtonGroup>
      }>
      <CollapsableColumns
        size="w-1/3"
        expand={isManualMovementFormOpen}
        contentLeft={
          <>
            <div className="mb-4 flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-2.5 shadow-sm border-l-4 border-l-[#2479B1]">
              <EnhancedDateRangePicker
                value={dateRange}
                onChange={handleDateRangeChange}
                onAccept={handleDateRangeAccept}
              />
              {!shouldHideTenantAdminSections && (
                <DeleteFilteredMovementsButton refresh={dataGridRefreshRef} />
              )}
            </div>
            <EnhancedDataGrid
              columns={columns}
              service={movementService}
              refresh={dataGridRefreshRef}
              outerParams={movementQueryParams}
              getMethod={movementService.getAggregated}
              disableDelete
            />
          </>
        }
        contentRight={
          <ManualMovement
            onSubmit={handleMovementSubmit}
            closeManualMovements={() => setManualMovementFormOpen(!isManualMovementFormOpen)}
          />
        }
      />
      <ProductBatchDialogDialog
        isOpen={isProductBatchDialogOpen}
        onClose={closeProductBatchDialog}
        onSubmit={submitMovement}
        selectedMovement={selectedMovement}
        setSelectedBatchId={setSelectedBatchId}
        batchExpirationOptions={batchExpirationOptions}
      />
    </TenantPage>
  );
};

export default Movements;
